import { type SpriteLayer } from './playerSprite';
import {
  DEFAULT_BODY,
  mountedClips,
  type BodyFrames,
  type EquipFrames,
  type PlayerFrames,
} from './playerArt';
import { equipment as equipDefs, isGhostBody, mountBody, SHROUD_ID } from '../sim/content';

/**
 * The render-content a character is drawn from: its body id and hue plus the
 * cosmetic equipment stacked over it (each its own id + optional dye hue). This is
 * exactly the {@link RenderableComponent} subset the layer assembly reads, so both
 * the live game ({@link GameRenderer.createSprite}) and the splash preview can pass
 * the same shape.
 */
export interface PlayerLook {
  /** UO body id selecting the baked animation set; absent falls back to the default. */
  body?: number;
  /** 1-based UO hue id applied to the body art (0 / absent = raw grayscale). */
  bodyHue?: number;
  /** Cosmetic equipment, each an `equipment` registry id + optional per-layer dye. */
  equipment?: { id: string; hue?: number }[];
  /**
   * The UO body id of the **mount** carrying this character, or absent when on foot
   * (ADR-0442). Drawn as the bottom layer of the stack, and switches the rider's own
   * body and equipment layers to their seated clips.
   */
  mount?: number;
}

/** Absolute rider lifts supplied by the admin's client-local tuning controls. */
export interface RiderHeightOverride {
  movingOffsetY?: number;
  idleOffsetY?: number;
}

type RiderOffset = number | readonly number[];

function offsetAt(offset: RiderOffset, facing: number): number {
  return typeof offset === 'number' ? offset : (offset[facing] ?? 0);
}

function idleCorrection(idle: RiderOffset, moving: RiderOffset): RiderOffset {
  if (typeof idle === 'number' && typeof moving === 'number') return idle - moving;
  return Array.from({ length: 8 }, (_, facing) =>
    offsetAt(idle, facing) - offsetAt(moving, facing));
}

/**
 * Assemble the ordered {@link SpriteLayer} stack a {@link PlayerSprite} draws from a
 * character's render-content: the chosen body is the base layer, each equipped
 * cosmetic is stacked over it, and every layer carries its own hue data so body
 * and equipment recolour independently. This is the single seam that turns
 * `renderable` data into a layer stack, shared by the live renderer and the splash
 * preview so the preview can never drift from the real result.
 *
 * The registry owns each item's canonical draw slot and per-layer hue path (full
 * vs partial cloth dye), and its body gating: an unknown/unbaked id is skipped
 * rather than crashing, and a layer whose `bodies` excludes the chosen body (a
 * beard on a 401, ADR-0031) never assembles.
 */
export function buildPlayerLayers(
  look: PlayerLook,
  bodyFrames: BodyFrames,
  equipFrames: EquipFrames,
  riderHeightOverride?: RiderHeightOverride,
): SpriteLayer[] {
  const body = look.body ?? DEFAULT_BODY;
  const bodyClips = bodyFrames[body] ?? bodyFrames[DEFAULT_BODY]!;

  // The ClassicUO death shroud (Duel deathfix, ADR-0142): a downed duelist carries a
  // ghost body id (the replicated dead marker), and is drawn as a **solid, grayscale,
  // hooded-robe** figure over the frozen die pose - the classic UO dead-human look -
  // *replacing* their normal clothing. The body renders off its correct-sex living
  // frames (`bodyFrames` already maps a ghost body to the living body) with **no hue
  // filter**: raw UO body art is grayscale, so dropping the hue *is* the grey render.
  // The death-shroud graphic ({@link SHROUD_ID}, ClassicUO anim 0x03CA - a single
  // hooded robe) is stacked on the robe layer, likewise ungreyed. Solidity is the
  // sprite default (the ghost translucency was removed with this look). The player's
  // own equipment is dropped for the dead period; resurrection swaps the body back to
  // living and the normal stack returns. The shroud is omitted only until its art is
  // baked (a clientless host / a bake that skipped it), leaving the bare grey body.
  if (isGhostBody(body)) {
    const shroud: SpriteLayer[] = [{ clips: bodyClips, layer: 'body' }];
    const shroudClips = equipFrames[SHROUD_ID];
    if (shroudClips && equipDefs.has(SHROUD_ID)) {
      shroud.push({ clips: shroudClips, layer: equipDefs.get(SHROUD_ID).layer });
    }
    return shroud;
  }

  // Mounted (ADR-0442): the mount's own body art becomes the bottom layer, and every
  // layer above it - the rider's body and each worn cosmetic - swaps its three
  // locomotion clips for the seated equivalents. `seated` is the per-layer transform
  // that does it; on foot it is the identity, so the unmounted path is untouched.
  //
  // The mount layer resolves through `bodyFrames` like any other body, so a mount whose
  // atlas is still streaming (or which was never baked) simply contributes no layer:
  // the rider is drawn on foot for a beat and pops onto the camel when it arrives,
  // rather than floating above a hole. The DEFAULT_BODY fallback the rider's own body
  // uses is deliberately NOT applied here - a missing camel must draw nothing, never a
  // second human under the rider.
  const mountClips = look.mount !== undefined ? bodyFrames[look.mount] : undefined;
  const mountDefinition = mountClips ? mountBody(look.mount) : undefined;
  const seated = mountClips ? mountedClips : (clips: PlayerFrames) => clips;
  // How far this particular animal lifts its rider. Read off the mount's registry row,
  // so a second mount with a different build seats its rider correctly by declaration
  // rather than by another branch here. Only applied when the mount actually drew.
  const riderLift: RiderOffset =
    riderHeightOverride?.movingOffsetY ?? mountDefinition?.riderOffsetY ?? 0;
  const absoluteIdleLift: RiderOffset =
    riderHeightOverride?.idleOffsetY ?? mountDefinition?.riderIdleOffsetY ?? riderLift;
  const riderIdleLift = mountDefinition
    ? idleCorrection(absoluteIdleLift, riderLift)
    : undefined;

  // The body is layer 0 on foot; mounted, the mount is under it. Equipment stacks over
  // (the final draw order is the facing-dependent one the PlayerSprite applies per layer).
  const layers: SpriteLayer[] = [];
  if (mountClips) {
    layers.push({
      clips: mountClips,
      layer: 'mount',
      hue: mountDefinition?.hue ?? 0,
      partialHue: false,
      visualScale: mountDefinition?.mountScale,
      scaleOrigin: 'ground',
    });
    if (mountDefinition?.mountPresentation === 'replacement') return layers;
  }
  layers.push({
    clips: seated(bodyClips),
    layer: 'body',
    hue: look.bodyHue ?? 0,
    partialHue: false,
    offsetY: riderLift,
    idleOffsetY: riderIdleLift,
  });
  for (const item of look.equipment ?? []) {
    const clips = equipFrames[item.id];
    if (!clips || !equipDefs.has(item.id)) continue; // unknown/unbaked layer: skip, don't crash
    // The registry owns the canonical draw slot; trust it over any client-sent layer.
    const def = equipDefs.get(item.id);
    // Gender gating (#29): a layer restricted to certain bodies (e.g. a beard,
    // `bodies: [400]`) never assembles on a body it isn't valid for, so a female
    // (401) character shows no beard even if one is somehow on its renderable.
    if (def.bodies && !def.bodies.includes(body)) continue;
    // Cloth dyes use the partial-hue path (near-gray pixels only); hair/beard,
    // like the body, are fully hued. The registry owns that per-layer choice.
    layers.push({
      clips: seated(clips),
      layer: def.layer,
      offsetY: riderLift,
      idleOffsetY: riderIdleLift,
      hue: item.hue ?? 0,
      partialHue: def.partialHue ?? false,
      visualScale: def.visualScale,
    });
  }
  return layers;
}
